import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ArrowLeftIcon } from "@/assets/icons";
import DatasetItem from "@/components/dataset/datasetList/DatasetItem";
import { Accordion } from "@/components/ui/accordion";
import { MAX_RANKING_INPUT } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { RootState } from "@/store";
import { clearSelectedDataset } from "@/store/slice/datasetSlice";
import { clearComparisonFeature } from "@/store/slice/mapSlice";
import {
  ESidebarMenuType,
  setAnalysisResultPopup,
  setShowAnalysisSummaryPopup,
  setShowComparisonPopup,
  setSideBarMenuTypePopupDisplay
} from "@/store/slice/uiSlice";
import { Dataset } from "@/types/dataset";
import { AreaInputType } from "@/types/spatial";
import { getAvailableDatasets } from "@/utils/dataset";

import { twMerge } from "tailwind-merge";

const noopFn = () => {};

const ComparePopup = () => {
  const dispatch = useDispatch();
  const { selectedDataset, datasets, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );
  const { spatialConfig } = useSelector((state: RootState) => state.spatial);
  const { showComparisonPopup } = useSelector((state: RootState) => state.ui);
  const { comparisonFeatureData } = useSelector(
    (state: RootState) => state.map
  );
  const availableDatasets = useMemo(
    () => getAvailableDatasets(datasets),
    [datasets]
  );

  const {
    handleToggleDatasetVisibility,
    handleChangeSelectedCategoryDataset,
    handleRankConfigChange,
    handleChangeStrokeAttributeDataset
  } = useDataset();
  const {
    handleChangeVisibility,
    handleClearSelectedState,
    renderDrawPolygon
  } = useMap();

  const [itemFocused, setItemFocused] = useState<Dataset | null>(null);
  const { isFromAnalysisResult, isFromAnalysisSummary } = showComparisonPopup;

  const handleBack = useCallback(() => {
    if (isFromAnalysisResult || isFromAnalysisSummary) {
      // Restore exactly the panels that were open before comparison mode,
      // so a summary the user had closed stays closed on the way back.
      dispatch(setAnalysisResultPopup(isFromAnalysisResult));
      dispatch(setShowAnalysisSummaryPopup(isFromAnalysisSummary));
    } else {
      dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
      if (spatialConfig?.areaInput?.value === AreaInputType.POLYGON) {
        renderDrawPolygon();
      }
    }

    comparisonFeatureData.forEach((feature) => {
      handleClearSelectedState(feature);
    });

    Object.entries(availableDatasets).forEach(([key, value]) => {
      handleChangeVisibility(
        key,
        value.style.visibility,
        value.data.spatial_aggregation_type,
        value.style.labelConfig.show_label,
        value.style.rankConfig?.isEnabled || false
      );
    });

    dispatch(clearSelectedDataset());
    dispatch(clearComparisonFeature());
    dispatch(
      setShowComparisonPopup({
        isShow: false,
        isFromAnalysisResult: false,
        isFromAnalysisSummary: false,
        isLimitShow: false
      })
    );
  }, [
    availableDatasets,
    comparisonFeatureData,
    dispatch,
    handleChangeVisibility,
    handleClearSelectedState,
    isFromAnalysisResult,
    isFromAnalysisSummary,
    renderDrawPolygon,
    spatialConfig
  ]);

  if (!selectedDataset?.data) return null;

  const dataset = availableDatasets[selectedDataset.data.id];
  const totalRows = rawDatasets[dataset.data.id]?.rows.length || 0;
  const maxRankingInput = Math.min(totalRows, MAX_RANKING_INPUT);

  return (
    <div
      className={twMerge(
        "absolute top-[30px] left-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[338px] max-h-[90vh]",
        "flex flex-col"
      )}
    >
      <div className="flex flex-row items-center p-4">
        <ArrowLeftIcon
          width={24}
          height={24}
          className="cursor-pointer"
          onClick={handleBack}
          data-testid="compare-popup-back-button"
        />
        <h2 className="text-xl font-medium text-txtHeading">
          Table Comparison
        </h2>
      </div>
      <div className="px-4 py-3">
        <div>
          <Accordion type="single" collapsible className="w-full">
            <ul className="flex flex-col gap-2 overflow-auto custom-scrollbar">
              <DatasetItem
                key={dataset.data.id}
                data={dataset}
                isComparsionMode={true}
                isFocused={dataset.data.id === itemFocused?.data.id}
                onMouseEnter={() => setItemFocused(dataset)}
                onMouseLeave={() => setItemFocused(null)}
                onRemoveDataset={noopFn}
                onOpenDatasetTabularModal={noopFn}
                isSpatialAnalysis={showComparisonPopup.isShow}
                onToggleDatasetVisibility={(e) => {
                  e.stopPropagation();
                  handleToggleDatasetVisibility(
                    dataset.data.id,
                    dataset.style.labelConfig.show_label,
                    dataset.style.rankConfig?.isEnabled || false
                  );
                }}
                handleChangeMaxRankingSlider={(value) => {
                  let newValue = value[0];
                  if (newValue < 1) newValue = 1;

                  handleRankConfigChange(dataset.data.id, dataset.style, {
                    maxRanking: newValue
                  });
                }}
                onSelectAttribute={(value) => {
                  handleChangeSelectedCategoryDataset(
                    dataset,
                    value,
                    dataset.style.colorFamily,
                    dataset.style.colorSteps
                  );
                }}
                onSelectStrokeAttribute={(value) => {
                  handleChangeStrokeAttributeDataset(
                    dataset,
                    value,
                    dataset.style.colorFamily
                  );
                }}
                onChangeColorStep={(step) => {
                  handleChangeSelectedCategoryDataset(
                    dataset,
                    dataset.selectedCategory.attribute_name,
                    dataset.style.colorFamily,
                    step
                  );
                }}
                onChangeStrokeColorStep={(step) => {
                  handleChangeStrokeAttributeDataset(
                    dataset,
                    dataset.style.stroke?.attribute_name || "",
                    dataset.style.colorFamily,
                    step
                  );
                }}
                handleChangeMaxRankingInput={(value) => {
                  if (value > maxRankingInput) value = maxRankingInput;
                  if (value < 1) value = 1;

                  handleRankConfigChange(dataset.data.id, dataset.style, {
                    maxRanking: value
                  });
                }}
                showLabel={dataset.style.labelConfig.show_label}
                handleRankConfigChangeOnSelectLabel={(isEnabled, category) => {
                  handleRankConfigChange(dataset.data.id, dataset.style, {
                    isEnabled,
                    category
                  });
                }}
                totalRows={totalRows}
                handleChangeRankChecked={(value) => {
                  handleRankConfigChange(dataset.data.id, dataset.style, {
                    isEnabled: value
                  });
                }}
              />
            </ul>
          </Accordion>
        </div>
      </div>
    </div>
  );
};
export default ComparePopup;
